<template>
    <div class="delivery">
        <div v-if="stockValidText" class="pharmacy-info">*{{ stockValidText }}</div>
        <div class="pharmacy-info">*{{ $t('pharmacy.pharmacyPriceInfo') }}</div>

        <div class="js-search-pharmacy-wrapper">
            <input id="js-search-pharmacy" class="form-control mb-4" :placeholder="$t('pharmacy.enterName')" />
        </div>

        <div v-if="data" class="pharmacy-skelet table-wrapper">
            <table v-if="loading">
                <tr v-for="index in 7" :key="index">
                    <td>&nbsp;</td>
                </tr>
            </table>
            <table v-else-if="$device.isDesktop && !loading">
                <tbody>
                    <tr>
                        <td class="table-header">{{ $t('pharmacy.pharmacyName') }}</td>
                        <td class="table-header">{{ $t('pharmacy.pharmacyAddress') }}</td>
                        <td class="table-header">{{ $t('pharmacy.quantity') }}</td>
                        <td class="table-header">{{ $t('header.detail') }}</td>
                    </tr>

                    <tr v-for="(pharmacy, key) in data" :id="`delivery-${key}`" :key="key">
                        <td>
                            <div class="text-nowrap">{{ pharmacy.name }}</div>
                        </td>
                        <td>
                            <div class="text-nowrap fs-14">{{ pharmacy.address }}</div>
                        </td>
                        <td class="d-flex" style="margin-left: 25px">
                            <div :class="pharmacy.qty > 3 ? 'text-success' : 'text-warning'">{{ formatQuantity(pharmacy.qty) }}</div>
                            <div :class="pharmacy.qty > 3 ? 'text-success' : 'text-warning'">&nbsp;{{ $t('pieces') }}</div>
                        </td>
                        <td>
                            <a class="pharmacy-list-page__detail-link" target="_blank" :href="pharmacy.url">{{
                                $t('pharmacy.showPharmacyDetail')
                            }}</a>
                        </td>
                    </tr>
                </tbody>
            </table>
            <table v-else-if="$device.isMobile && !loading">
                <tbody>
                    <tr v-for="(pharmacy, key) in data" :id="`delivery-${key}`" :key="key">
                        <td>
                            <div class="d-flex">
                                <div class="col-8">
                                    <div>{{ pharmacy.name }}</div>
                                    <div class="mt-2 fs-14" style="font-weight: 400">{{ pharmacy.address }}</div>
                                </div>
                                <div class="ml-auto d-flex align-items-center">
                                    <div :class="pharmacy.qty > 3 ? 'text-success' : 'text-warning'">
                                        {{ formatQuantity(pharmacy.qty) }}
                                    </div>
                                    <div :class="pharmacy.qty > 3 ? 'text-success' : 'text-warning'">&nbsp;{{ $t('pieces') }}</div>
                                </div>
                            </div>
                            <div class="text-center mt-3 mb-1">
                                <a class="pharmacy-list-page__detail-link" target="_blank" :href="pharmacy.url">{{
                                    $t('pharmacy.showPharmacyDetail')
                                }}</a>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-else class="alert alert-danger px-5">{{ $t('error') }}</div>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import PharmacyProductAvailability from '~/models/Api/Pharmacy/PharmacyProductAvailability'

export default {
    props: {
        productId: {
            type: Number,
            required: true,
        },
        pharmacies: {
            type: Array as PropType<PharmacyProductAvailability[]>,
            required: false,
            default: null,
        },
        pharmacyText: {
            type: String,
            required: false,
            default: null,
        },
    },

    data() {
        return {
            data: [] as PharmacyProductAvailability[],
            stockValidText: '',
            loading: true,
        }
    },

    async mounted() {
        if (!this.pharmacies) {
            await this.fetch()
        } else {
            this.data = this.pharmacies
            this.stockValidText = this.pharmacyText
            this.loading = false
        }

        let searchTimeout: NodeJS.Timeout
        document.getElementById('js-search-pharmacy')?.addEventListener(
            'input',
            function () {
                clearTimeout(searchTimeout)
                searchTimeout = setTimeout(async () => {
                    let pharmacyName: string | null = (document.getElementById('js-search-pharmacy') as HTMLInputElement).value
                    if (pharmacyName.length < 3) {
                        pharmacyName = null
                    }
                    const response = await this.$repository.getPharmacyProductsAvailability(this.productId, pharmacyName)

                    this.data = response.pharmacyProductAvailability
                }, 500)
            }.bind(this)
        )
    },

    methods: {
        async fetch(): Promise<void> {
            const response = await this.$repository.getPharmacyProductsAvailability(this.productId)
            this.data = response.pharmacyProductAvailability
            this.stockValidText = response.stockValidText
            this.loading = false
        },

        formatQuantity(qty: number) {
            if (qty > 10) {
                return '10+'
            }

            return qty
        },
    },
}
</script>

<style lang="scss">
.pharmacy-skelet {
    table tbody {
        tr {
            td:nth-child(2) {
                .b-skeleton-text {
                    float: right;
                    max-width: 30%;
                }
            }
        }
    }
}

.modal-pharmacies {
    .pharmacy-info {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 1rem;
        color: $color-cta;

        @media (max-width: 768px) {
            padding: 0 1rem 0 1rem;
        }
    }

    .js-search-pharmacy-wrapper {
        @media (max-width: 768px) {
            padding: 0 1rem 0 1rem;
        }
    }

    #js-search-pharmacy {
        border-radius: 100px;
        background: #fff url('/nuxt-assets/icons/search.svg') no-repeat;
        background-position: 16px 7px !important;
        background-size: 15px 20px;
        border: none;
        box-shadow: 0 4px 19px -8px #00000054;
        height: 35px;
        outline: 0;
        padding: 1px 25px 4px 40px;
        position: relative;
        transition: 0.15s ease-in-out;
        width: 100%;
    }

    .table-header {
        font-weight: 590;
    }

    .modal-body {
        padding: 0;
    }

    .modal-dialog {
        max-width: 1200px;
    }

    .modal-header {
        @media (max-width: 768px) {
            padding: 0 1rem 0 1rem !important;
        }
    }

    &.modal {
        .modal-content {
            overflow: hidden;
            box-shadow: none;
        }

        .modal-title {
            font-size: 16px;
        }
    }

    .modal-content {
        @media (max-width: 768px) {
            padding: 0 !important;
        }

        .table-wrapper {
            @media (min-width: 768px) {
                margin: 0 -1.5em -1.5em;
            }

            td {
                border-width: 0;
            }
        }

        table {
            width: 100%;
            font-weight: 500;
            margin-bottom: 10px;

            tr {
                background-color: $color-grey;
            }

            tr:nth-child(even) {
                background-color: transparent;
            }

            td {
                padding: 0.75em 1.5em;

                @media (max-width: 480px) {
                    margin-bottom: -1.5em;
                    padding: 0.75em 1.5em;
                }
            }
        }
    }
}
</style>
